import { useRouter } from 'vue-router'
import { useToast } from '@/components/ui/toast'
import { ROUTES } from '@/router/routes'
import { DocumentTypeEnum } from '@/types/generated'

/**
 * Composable for Office document utilities and editing functionality
 *
 * Provides utilities for detecting, validating, and opening Office documents
 * for editing in the OnlyOffice editor.
 */
export function useOfficeDocument() {
  const router = useRouter()
  const { toast } = useToast()

  /**
   * Check if a file is an Office document based on filename extension
   *
   * Supports: .docx, .doc, .xlsx, .xls, .pptx, .ppt
   */
  const isOfficeFile = (document: { filename?: string }): boolean => {
    if (!document.filename) return false

    const filename = document.filename.toLowerCase()
    return (
      filename.endsWith('.docx') ||
      filename.endsWith('.doc') ||
      filename.endsWith('.xlsx') ||
      filename.endsWith('.xls') ||
      filename.endsWith('.pptx') ||
      filename.endsWith('.ppt')
    )
  }

  /**
   * Check if a document is editable (Office file and not a questionnaire)
   *
   * Questionnaires (FRAGEBOGEN) should not be editable via Office editor
   */
  const isEditableDocument = (document: { document_type: string; filename?: string }): boolean => {
    return document.document_type !== DocumentTypeEnum.FRAGEBOGEN && isOfficeFile(document)
  }

  /**
   * Open an Office file in the OnlyOffice editor
   *
   * Validates the document is an Office file before routing to editor
   */
  const openOfficeEditor = (document: { uuid: string; filename?: string }) => {
    if (!isOfficeFile(document)) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'This document cannot be opened in the office editor.'
      })
      return
    }

    router.push(ROUTES.APP.OFFICE_EDITOR.replace(':documentUuid', document.uuid))
  }

  /**
   * Get appropriate icon name for Office file type
   *
   * Returns Lucide icon names for different Office file types
   */
  const getOfficeFileIcon = (filename?: string): string => {
    if (!filename) return 'FileText'

    const lower = filename.toLowerCase()
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'FileText'
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'Sheet'
    if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) return 'Presentation'

    return 'FileText'
  }

  /**
   * Get user-friendly file type description
   */
  const getOfficeFileType = (filename?: string): string => {
    if (!filename) return 'Document'

    const lower = filename.toLowerCase()
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'Word Document'
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'Excel Spreadsheet'
    if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) return 'PowerPoint Presentation'

    return 'Document'
  }

  /**
   * Get color classes for Office file type indicators
   *
   * Returns Tailwind CSS classes for consistent Office file type styling
   */
  const getOfficeFileColors = (filename?: string) => {
    if (!filename)
      return {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-300'
      }

    const lower = filename.toLowerCase()

    // Word documents - Blue theme
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) {
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200'
      }
    }

    // Excel spreadsheets - Green theme
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      return {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200'
      }
    }

    // PowerPoint presentations - Orange theme
    if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) {
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200'
      }
    }

    return {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-300'
    }
  }

  /**
   * Get accessibility label for Office file type
   *
   * Provides screen reader friendly descriptions
   */
  const getOfficeFileAriaLabel = (filename?: string, isEditable = false): string => {
    const fileType = getOfficeFileType(filename)
    const editableText = isEditable ? ' (editable)' : ''
    return `${fileType}${editableText}`
  }

  return {
    isOfficeFile,
    isEditableDocument,
    openOfficeEditor,
    getOfficeFileIcon,
    getOfficeFileType,
    getOfficeFileColors,
    getOfficeFileAriaLabel
  }
}
